import React, { useRef, useState } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Box,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AccessControl from "../AccessControl";

const defaultItemSx = {
  fontSize: "14px",
  py: 1.5,
  px: 2,
  minHeight: "auto",
  borderRadius: "6px",
  mx: 0.5,
  "&:hover": { backgroundColor: "#F3F4F6" },
};

const defaultMenuPaperSx = {
  "& .MuiPaper-root": {
    mt: 1,
    minWidth: "180px",
    maxWidth: "220px",
    boxShadow:
      "0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)",
    borderRadius: "8px",
    border: "1px solid #E5E7EB",
    py: 0.5,
  },
};


const defaultSubmenuPaperSx = {
  mt: 0,
  ml: 0.5,
  minWidth: "160px",
  boxShadow:
    "0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)",
  borderRadius: "8px",
  border: "1px solid #E5E7EB",
  py: 0.5,
};

/**
 * Reusable 3-dot actions menu: IconButton + Menu.
 * Items can be flat or nested (submenu).
 *
 * Item shape:
 * - Simple: { label, icon?, onClick }
 * - With submenu: { label, icon?, endIcon?, onClick?, children: [{ label, icon?, onClick }, ...] }
 *   When children is present and non-empty, hovering shows submenu. onClick runs when item is clicked and has no children (or when submenu is not shown).
 *
 * @param {Array<{ label: string, icon?: React.ReactNode, endIcon?: React.ReactNode, onClick?: () => void, children?: Array<{ label: string, icon?: React.ReactNode, onClick: () => void }> }>} [items]
 * @param {React.ReactNode|((close: () => void) => React.ReactNode)} [children] - custom content; if function, receives close()
 * @param {string} [ariaLabel]
 * @param {object} [buttonSx]
 * @param {object} [menuSx]
 * @param {object} [itemSx] - sx applied to each MenuItem
 * @param {object} [submenuPaperSx] - sx for nested Menu Paper
 * @param {React.ReactNode} [icon]
 * @param {object} [anchorOrigin]
 * @param {object} [transformOrigin]
 * @param {() => void} [onClose]
 */
const ActionsMenu = ({
  items,
  children,
  ariaLabel = "Actions",
  buttonSx,
  menuSx,
  itemSx,
  submenuPaperSx,
  icon,
  anchorOrigin = { vertical: "bottom", horizontal: "right" },
  transformOrigin = { vertical: "top", horizontal: "right" },
  onClose: onCloseProp,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);
  const [submenuOpenIndex, setSubmenuOpenIndex] = useState(null);
  const submenuCloseTimeoutRef = useRef(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => {
    setAnchorEl(null);
    setSubmenuAnchorEl(null);
    setSubmenuOpenIndex(null);
    if (submenuCloseTimeoutRef.current) {
      clearTimeout(submenuCloseTimeoutRef.current);
      submenuCloseTimeoutRef.current = null;
    }
    onCloseProp?.();
  };

  const clearSubmenuCloseTimeout = () => {
    if (submenuCloseTimeoutRef.current) {
      clearTimeout(submenuCloseTimeoutRef.current);
      submenuCloseTimeoutRef.current = null;
    }
  };

  const handleSubmenuItemEnter = (event, hasChildren, index) => {
    clearSubmenuCloseTimeout();
    if (hasChildren) {
      setSubmenuAnchorEl(event.currentTarget);
      setSubmenuOpenIndex(index);
    }
  };

  const handleSubmenuItemLeave = () => {
    submenuCloseTimeoutRef.current = setTimeout(() => {
      setSubmenuAnchorEl(null);
      setSubmenuOpenIndex(null);
    }, 150);
  };

  const runItemClick = (item, isNested = false) => {
    item.onClick?.();
    handleClose();
  };

  const mergedItemSx = { ...defaultItemSx, ...itemSx };

  const renderItem = (item, index, isNested = false) => {
    const hasChildren = Array.isArray(item.children) && item.children.length > 0;

    if (isNested) {
      const node = (
        <MenuItem onClick={() => runItemClick(item, true)} sx={mergedItemSx}>
          {item.icon != null ? (
            <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
          ) : null}
          <ListItemText primary={item.label} />
        </MenuItem>
      );
      return item.featureId ? (
        <AccessControl key={index} featureId={item.featureId} wrapperSx={{ display: "block", width: "100%" }}>{node}</AccessControl>
      ) : (
        React.cloneElement(node, { key: index })
      );
    }

    if (hasChildren) {
      const node = (
        <MenuItem
          onMouseEnter={(e) => handleSubmenuItemEnter(e, true, index)}
          onMouseLeave={handleSubmenuItemLeave}
          onClick={() => item.onClick?.()}
          sx={mergedItemSx}
        >
          {item.icon != null ? (
            <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
          ) : null}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: 0.5,
            }}
          >
            <ListItemText primary={item.label} />
            {item.endIcon ?? (
              <KeyboardArrowDownIcon sx={{ fontSize: "16px", color: "#6B7280", ml: 0.5 }} />
            )}
          </Box>
        </MenuItem>
      );
      return item.featureId ? (
        <AccessControl key={index} featureId={item.featureId} wrapperSx={{ display: "block", width: "100%" }}>{node}</AccessControl>
      ) : (
        React.cloneElement(node, { key: index })
      );
    }

    const node = (
      <MenuItem onClick={() => runItemClick(item)} sx={mergedItemSx}>
        {item.icon != null ? (
          <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
        ) : null}
        <ListItemText primary={item.label} />
      </MenuItem>
    );
    return item.featureId ? (
      <AccessControl key={index} featureId={item.featureId} wrapperSx={{ display: "block", width: "100%" }}>{node}</AccessControl>
    ) : (
      React.cloneElement(node, { key: index })
    );
  };

  const menuContent =
    items != null
      ? (() => {
          const nodes = items.map((item, index) => renderItem(item, index, false));
          const submenuItem =
            submenuOpenIndex != null && items[submenuOpenIndex]?.children
              ? items[submenuOpenIndex]
              : null;

          return (
            <>
              {nodes}
              {submenuItem && (
                <Menu
                  disablePortal
                  anchorEl={submenuAnchorEl}
                  open={Boolean(submenuAnchorEl)}
                  onClose={() => {
                    setSubmenuAnchorEl(null);
                    setSubmenuOpenIndex(null);
                  }}
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  onMouseEnter={clearSubmenuCloseTimeout}
                  onMouseLeave={handleSubmenuItemLeave}
                  sx={{
                    pointerEvents: submenuAnchorEl ? "auto" : "none",
                    "& .MuiPaper-root": submenuPaperSx ?? defaultSubmenuPaperSx,
                  }}
                >
                  {submenuItem.children.map((child, idx) =>
                    renderItem(child, idx, true)
                  )}
                </Menu>
              )}
            </>
          );
        })()
      : typeof children === "function"
        ? children(handleClose)
        : children;

  return (
    <>
      <IconButton
        aria-label={ariaLabel}
        aria-controls={open ? "actions-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleOpen}
        sx={{ color: "text.secondary", ...buttonSx }}
      >
        {icon ?? <MoreVertIcon />}
      </IconButton>
      <Menu
        id="actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        sx={menuSx ?? defaultMenuPaperSx}
      >
        {menuContent}
      </Menu>
    </>
  );
};

export default ActionsMenu;
